import React, { useContext } from 'react';
import T from 'i18n-react';
import dynamic from 'next/dynamic';

import { Badge } from '@motorway/mw-highway-code';
import { VRM } from '@motorway/mw-highway-code/alpha';

import { isCompletedPage } from 'Components/Account/Purchases/Purchases.helpers';
import useAccountRouter from 'Components/Account/useAccountRouter';
import cypressIds from 'Components/cypress_ids.json';
import { ACCOUNT_PAGE_CATEGORIES, ACCOUNT_SLUGS } from 'Components/pages/AccountPage/AccountPage.consts';
import { MonetaryValue } from 'Components/pages/Wallet/UI/MonetaryValue';
import { VehicleTradeBadge } from 'Components/shared/VehicleTradeBadge/VehicleTradeBadge';
import { FEATURES } from 'Context/FeaturesContext/FeaturesContextConsts';
import { VehicleContext } from 'Context/VehicleContext';
import { WithAccountPermitsChecker } from 'Controllers/AccountPermitsChecker/AccountPermitsChecker.helpers';
import { formatVehicleName } from 'Utilities/formatters';
import { applyCypressData } from 'Utilities/helpers';
import useFeatureToggle from 'Utilities/hooks/useFeatureToggle';
import proPropTypes from 'Utilities/proPropTypes';

import AccountVehicleCardImage from '../AccountVehicleCardImage/AccountVehicleCardImage';

import BiddingCountdown from './BiddingCountdown/BiddingCountdown';
import MoreMenuComposer from './MoreMenuComposer/MoreMenuComposer';
import PriceDetails, { PRICE_DETAILS_TYPES } from './PriceDetails/PriceDetails';
import { getVehicleStateTagLabel, useCreatedAtBidFieldLogic } from './AccountVehicleCardView.helpers';

import styles from './AccountVehicleCardView.module.scss';

T.setTexts(require('Components/texts.json'));

const StateExplorer = dynamic(
	() =>
		import(/* webpackChunkName: "state_explorer" */ 'Components/FeaturesEditor/plugins/StateExplorer/StateExplorer'),
);

const AccountVehicleCardView = ({ permits, triggerSuccessToast, vehicle }) => {
	const vehicleStateExplorer = useFeatureToggle(FEATURES.vehicleStateExplorer);

	const { category, slug } = useAccountRouter();
	const displayPurchaseStatusTag = category === ACCOUNT_PAGE_CATEGORIES.PURCHASES && slug === ACCOUNT_SLUGS.COMPLETE;

	const { id } = useContext(VehicleContext);

	const vehicleName = formatVehicleName(vehicle);

	const { enquirySoldDate, hasFees, isDelivered, isTrade, leadPhoto, prettyVrm, state, totalPaymentAmount, userBid } =
		vehicle;

	const time = useCreatedAtBidFieldLogic({
		className: styles.createdAt,
		value: isCompletedPage(slug) ? enquirySoldDate : userBid?.createdAt,
	});

	return (
		<div
			{...applyCypressData(`${cypressIds.links.accountSectionVehicleCard}-${id}`)}
			className={styles.accountVehicleCardView}
		>
			<header className={styles.header}>
				<AccountVehicleCardImage alt={vehicleName} photo={leadPhoto} />
				<div className={styles.vehicleInfo}>
					<div className={styles.vrm}>
						<VRM size="small" text={prettyVrm} />
						{time}
					</div>
					<h4 className={styles.vehicleName} {...applyCypressData(cypressIds.text.accountSectionVehicleName)}>
						{vehicleName}
					</h4>
				</div>
			</header>
			<div className={styles.bidArea}>
				<span {...applyCypressData(cypressIds.text.accountSectionPrice)}>
					<PriceDetails />
				</span>
				{hasFees && (
					<span>
						{T.translate(`account.vehicleCard.total`, {
							value: (
								<strong>
									<MonetaryValue value={totalPaymentAmount} />
								</strong>
							),
						})}
					</span>
				)}
				{permits.displayMaxBid() ? (
					<span className={styles.maxBid}>
						<PriceDetails type={PRICE_DETAILS_TYPES.MAX_BID} />
					</span>
				) : null}
			</div>
			<div className={styles.badges}>
				{isCompletedPage(slug) && isDelivered ? <Badge text="Delivered" variant="success" /> : null}
				{permits.displayBiddingCountdown() ? <BiddingCountdown /> : null}
				{permits.displaySoldFor() ? (
					<Badge
						{...applyCypressData(cypressIds.lists.accountSoldForTag)}
						text={<PriceDetails type={PRICE_DETAILS_TYPES.SOLD_FOR} />}
						variant="primary"
					/>
				) : null}
				{displayPurchaseStatusTag && permits.displayTransportRequestedTag() ? (
					<Badge text={T.translate('account.vehicleCard.transportRequested')} variant="primary" />
				) : null}
				{displayPurchaseStatusTag && permits.displayStateTag() ? (
					<Badge text={T.translate(getVehicleStateTagLabel(state.slug))} variant="primary" />
				) : null}
				{permits.displayCancelledLabel() ? (
					<Badge text={T.translate('account.vehicleCard.cancelled')} variant="primary" />
				) : null}
				{isTrade ? <VehicleTradeBadge /> : null}
				{vehicleStateExplorer ? <StateExplorer isLarge /> : null}
			</div>
			{permits.isPurchaseCompletePage() ? (
				<div className={styles.moreArea}>
					<MoreMenuComposer triggerSuccessToast={triggerSuccessToast} />
				</div>
			) : null}
		</div>
	);
};

AccountVehicleCardView.propTypes = {
	permits: proPropTypes.accountPermitsChecker.isRequired,
	vehicle: proPropTypes.accountVehicle.isRequired,
};

AccountVehicleCardView.displayName = 'AccountVehicleCardView';

export default WithAccountPermitsChecker()(AccountVehicleCardView);
